import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { Button } from 'react-bootstrap';

import './PurchaseItemSection.scss';

import CustomTitleWithSwitch from 'components/base/CustomTitleWithSwitch';
import { DISCOUNT_TYPE, DISCOUNT_VALUE_TYPE } from 'config/CustomEnums';
import { TRANSACTION_EVENT_TYPE } from 'containers/record/records/TransactionList';
import DiscountSection from './DiscountSection';
import TransactionAdditionalData from './TransactionAdditionalData';
import CustomTitleWithInput from '../base/CustomTitleWithInput';
import { hasError, ReactHookFormErrorMessage } from '../base/ErrorFieldMessage';
import { CustomTitleLabel } from '../earning/CustomBaseComponments';
import { CreateTransactionError } from 'models/CreateTransactionModel';


function PurchaseItemSection({
  index = 0,
}) {
  const { watch, setValue, formState, clearErrors } = useFormContext();
  const { errors } = formState;

  const purchasedItemsToDeleteField = 'purchasedItemsToDelete';
  const purchasedItemsField = 'purchasedItems';
  const currentPurchasedItemField = `${purchasedItemsField}.${index}`
  const productNameField = `${currentPurchasedItemField}.productName`;
  const skuField = `${currentPurchasedItemField}.sku`;
  const categoryField = `${currentPurchasedItemField}.category`;
  const subcategoryField = `${currentPurchasedItemField}.subcategory`;
  const brandField = `${currentPurchasedItemField}.brand`;
  const quantityField = `${currentPurchasedItemField}.quantity`;
  const originalPriceField = `${currentPurchasedItemField}.originalPrice`
  const valueField = `${currentPurchasedItemField}.value`;
  const itemDiscountListField = `${currentPurchasedItemField}.discounts`;
  const itemAdditionalDataField = `${currentPurchasedItemField}.additionalData`;
  const isRefundedField = `${currentPurchasedItemField}.isRefunded`;
  const refundedQuantityField = `${currentPurchasedItemField}.refundedQuantity`;
  const netSalesField = `${currentPurchasedItemField}.netSales`;

  const purchasedItemsToDelete = watch(purchasedItemsToDeleteField) || [];
  const currentPurchasedItem = watch(currentPurchasedItemField);
  const purchasedItems = watch(purchasedItemsField);
  const productName = watch(productNameField);
  const sku = watch(skuField);
  const category = watch(categoryField);
  const subcategory = watch(subcategoryField);
  const brand = watch(brandField);
  const quantity = watch(quantityField);
  const originalPrice = watch(originalPriceField);
  const value = watch(valueField);
  const itemDiscountList = watch(itemDiscountListField);
  const isRefunded = watch(isRefundedField);
  const refundedQuantity = watch(refundedQuantityField);
  const displayOfflineEventType = watch('displayOfflineEventType');
  const netSales = watch(netSalesField);

  useEffect(() => {
    if (!itemDiscountList || itemDiscountList?.length < 1) {
      setValue(itemDiscountListField, [{
        type: DISCOUNT_TYPE.ITEM,
        valueType: DISCOUNT_VALUE_TYPE.PERCENTAGE,
        value: null,
        code: null,
      }], { shouldDirty: true, });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <div className="d-flex">
        <div className="first-section-title">{`PURCHASED ITEM #${index + 1}`}</div>
        {purchasedItems.length > 1
          ? (
            <button
              className="base-delete-button base-back-button"
              onClick={() => {
                let itemList = purchasedItems;
                clearErrors()
                itemList.splice(index, 1);
                setValue(purchasedItemsField, itemList, { shouldDirty: true });
                if (currentPurchasedItem?.pk) {
                  setValue(purchasedItemsToDeleteField, [...purchasedItemsToDelete, currentPurchasedItem?.pk], { shouldDirty: true })
                }
              }}
            >
              Delete
            </button>
          )
          : null}
      </div>

      <CustomTitleWithInput
        title={'Product name'}
        defaultValue={productName}
        useDefaultValue={false}
        setValue={(value) => {
          setValue(productNameField, value, { shouldDirty: true, });
        }}
        error={{
          id: productNameField,
          error: hasError(errors, productNameField),
          message: CreateTransactionError.purchasedItemName.message,
        }}
      />

      <CustomTitleWithInput
        title={'Sku (optional)'}
        defaultValue={sku}
        useDefaultValue={false}
        setValue={(value) => {
          setValue(skuField, value, { shouldDirty: true, });
        }}
      />

      <CustomTitleWithInput
        title={'Category (optional)'}
        defaultValue={category}
        useDefaultValue={false}
        setValue={(value) => {
          setValue(categoryField, value, { shouldDirty: true, });
        }}
      />

      <CustomTitleWithInput
        title={'Subcategory (optional)'}
        defaultValue={subcategory}
        useDefaultValue={false}
        setValue={(value) => {
          setValue(subcategoryField, value, { shouldDirty: true, });
        }}
      />

      <CustomTitleWithInput
        title={'Brand (optional)'}
        defaultValue={brand}
        useDefaultValue={false}
        setValue={(value) => {
          setValue(brandField, value, { shouldDirty: true, });
        }}
      />

      <CustomTitleWithInput
        type='number'
        title="Quantity"
        defaultValue={quantity}
        useDefaultValue={false}
        setValue={(value) => {
          setValue(quantityField, value, { shouldDirty: true, });
        }}
        unit="number of items"
        customClass={`margin-right-10-percent ${hasError(errors, quantityField) ? "error-field" : ""}`}
      />
      <ReactHookFormErrorMessage errors={errors} id={quantityField} />

      <CustomTitleWithInput
        type='number'
        title="Original price (optional)"
        defaultValue={originalPrice}
        useDefaultValue={false}
        setValue={(value) => {
          setValue(originalPriceField, value, { shouldDirty: true, });
        }}
        per="HK$"
      />

      <CustomTitleWithInput
        type='number'
        title="Final price"
        defaultValue={value}
        useDefaultValue={false}
        setValue={(value) => {
          setValue(valueField, value, { shouldDirty: true, });
        }}
        per="HK$"
        customClass={
          hasError(errors, valueField) ? "error-field" : ""
        }
      />
      <ReactHookFormErrorMessage errors={errors} id={valueField} />

      <CustomTitleWithInput
        type='number'
        title="Item net sales (optional)"
        defaultValue={netSales}
        useDefaultValue={false}
        setValue={(value) => {
          setValue(netSalesField, value, { shouldDirty: true, });
        }}
        per="HK$"
      />

      <div className='multiple-discounts'>
        {itemDiscountList?.map((value, index) => <DiscountSection index={index} discountListField={itemDiscountListField} />)}
        <div>
          <Button
            onClick={() => {
              const tempArray = [].concat(itemDiscountList);
              const value = {
                type: DISCOUNT_TYPE.ITEM,
                valueType: DISCOUNT_VALUE_TYPE.PERCENTAGE,
                value: null,
                code: null,
              };
              tempArray.push(value);
              setValue(itemDiscountListField, tempArray, { shouldDirty: true, });
            }}
            className="btn-add-button-common"
          >
            Add discount
          </Button>
        </div>
      </div>
      <div className='item-addtional-data'>
        <TransactionAdditionalData additionalDataField={itemAdditionalDataField} />
      </div>
      {displayOfflineEventType === TRANSACTION_EVENT_TYPE.TYPE_PARTIALLY_REFUNDED &&
        <CustomTitleWithSwitch
          title={'Is it refunded?'}
          defaultValue={isRefunded || false}
          setValue={(value) => {
            setValue(isRefundedField, value, { shouldDirty: true });
          }}
        />
      }

      {
        displayOfflineEventType === TRANSACTION_EVENT_TYPE.TYPE_PARTIALLY_REFUNDED && isRefunded && (
          <>
            <CustomTitleWithInput
              type='number'
              title="Refunded quantity"
              defaultValue={refundedQuantity}
              useDefaultValue={false}
              setValue={(value) => {
                setValue(refundedQuantityField, value, { shouldDirty: true, });
              }}
              unit="number of items"
              customClass={`margin-right-10-percent ${hasError(errors, refundedQuantityField) ? "error-field" : ""}`}
            />
            <ReactHookFormErrorMessage errors={errors} id={refundedQuantityField} />
          </>
        )
      }


      {index === purchasedItems.length - 1
        ? (
          <div className="content-section-table-container">
            <Button
              onClick={() => {
                const tempArray = [].concat(purchasedItems);
                const value = {
                  productName: null,
                  sku: null,
                  category: null,
                  brand: null,
                  quantity: null,
                  value: null,
                };
                tempArray.push(value);
                setValue(purchasedItemsField, tempArray, { shouldDirty: true, });
              }}
              className="btn-add-button-common"
            >
              Add Purchased Item
            </Button>
          </div>
        )
        : null}
    </>
  );
}

export default PurchaseItemSection;
